'use client';

import { useEffect } from 'react';

const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY;

let initDone = false;

/**
 * Env-gated PostHog bootstrap (session replay + product analytics).
 *
 * posthog-js is NOT statically imported: it (plus the recorder + surveys
 * side-loads its init triggers) was ~100KB of script evaluated during
 * hydration on every marketing page. Instead the SDK is dynamically imported
 * on the visitor's FIRST interaction (same listener set as the gtag/Pixel
 * stubs in app/layout.tsx), with a 10s post-mount fallback so non-interacting
 * visitors are still counted. The age gate guarantees a tap from every real
 * visitor, so pageview capture is effectively unchanged — it just no longer
 * competes with first paint.
 *
 * When NEXT_PUBLIC_POSTHOG_KEY is unset this renders children only — no SDK,
 * no listeners, zero overhead.
 */
export function PostHogProvider({ children }: React.PropsWithChildren) {
  useEffect(() => {
    if (!POSTHOG_KEY || initDone) {
      return;
    }

    const events = ['pointerdown', 'keydown', 'touchstart', 'scroll'] as const;

    const start = () => {
      if (initDone) return;
      initDone = true;
      for (const e of events) window.removeEventListener(e, start);
      clearTimeout(fallback);
      void initPostHog();
    };

    for (const e of events) {
      window.addEventListener(e, start, { once: true, passive: true });
    }

    const fallback = setTimeout(start, 10_000);

    return () => {
      for (const e of events) window.removeEventListener(e, start);
      clearTimeout(fallback);
    };
  }, []);

  return <>{children}</>;
}

async function initPostHog() {
  const { default: posthog } = await import('posthog-js');

  posthog.init(POSTHOG_KEY!, {
    api_host:
      process.env.NEXT_PUBLIC_POSTHOG_HOST ?? 'https://us.i.posthog.com',
    capture_pageview: true,
    capture_pageleave: true,
    // Auto-capture uncaught JS errors + unhandled rejections so real
    // bugs surface in PostHog Error Tracking (was off — 0 exceptions).
    capture_exceptions: true,
    session_recording: { maskAllInputs: true },
    persistence: 'localStorage+cookie',
    // Keep Error Tracking signal clean: drop benign framework noise so
    // real bugs aren't buried. AbortErrors are react-query/Next cancelling
    // in-flight fetches on unmount/navigation (2 users/wk, harmless);
    // removeChild NotFoundErrors are browser-extension/Google-Translate
    // DOM edits colliding with React.
    before_send: (event) => {
      if (event?.event === '$exception') {
        const values = JSON.stringify(event.properties?.$exception_list ?? '');
        if (
          values.includes('AbortError') ||
          values.includes("Failed to execute 'removeChild'")
        ) {
          return null;
        }
      }
      return event;
    },
  });

  // Expose globally so trackFunnelEvent (a plain module with no posthog
  // import) can mirror funnel events into PostHog when it is enabled.
  (window as Window & { posthog?: typeof posthog }).posthog = posthog;

  // Identify by Supabase user id only — no email or other PII. getSession
  // reads the cached local session; this runs post-interaction so it never
  // competes with hydration.
  try {
    const { getSupabaseBrowserClient } =
      await import('@kit/supabase/browser-client');
    const { data } = await getSupabaseBrowserClient().auth.getSession();
    const userId = data.session?.user.id;

    if (userId) {
      posthog.identify(userId);
    }
  } catch {
    // Analytics must never affect the app.
  }
}
